import { Component, h, Element, Prop, Listen } from '@stencil/core';
import { guid } from 'utils';
import Glide from '@glidejs/glide'

@Component({
    tag: 'app-hero-slider',
    styleUrl: 'app-hero-slider.scss',
    shadow: false
})
export class AppHeroSlider {

    @Element() root: HTMLElement;

    private sliderId = 'hero-slider-' + guid();
    private initialRenderComplete = false;
    private slider: any = null;

    @Prop() pauseIcon: string = "pause";
    @Prop() pauseLabel: string = "Pause";  
    /**
    * If true, bullet nav will display below slides. Default will show bullet
    * nav within slides area.
    */
   @Prop() displayBulletNavExternally: boolean;


    @Listen('sliderPaused')
    pauseBtnListener(event: CustomEvent<boolean>) {
        if (event.detail === true) {
            this.slider.pause();
            this.pauseIcon = "play";
            this.pauseLabel = "Play";
        } else {
            this.slider.play();
            this.pauseIcon = "pause";
            this.pauseLabel = "Pause";
        }
    }

    private setUp() {
        if (document.querySelector('body.is-editing'))
            return;

        const instance = document.getElementById(this.sliderId);
        if(!instance) return;
        const bulletsWrapper = instance.querySelectorAll('.glide__bullets');
        const glideSlides = instance.querySelector('.glide__slides');
        if(!glideSlides) return;
        const slides = [...glideSlides.children];
        // const pauseBtn = instance.querySelector('.st-carousel-inner > .bullets-wrapper > .hero-slider-pause');
        const autoplay = slides.length > 1 ? 6000 : false;

        if (slides.length <= 1) {
            instance.classList.add('hide-controls');
        }
        bulletsWrapper.forEach((wrapper) => {
            for (let i = 0; i < slides.length; i++) {
                wrapper.innerHTML += `
                <button class="glide__bullet" data-glide-dir="=${i}" aria-label="Go to slide ${i + 1}" tabindex="0" aria-controls=${this.sliderId + '-slides'}></button>`;
            }
        })
        
        this.slider = new Glide(`#${this.sliderId}`, {
            type: 'carousel',
            perView: 1,
            focusAt: '0',
            autoplay: autoplay,
            hoverpause: false,
            swipeThreshold: false,
            dragThreshold: false,
            bound: true,
            gap: 0
        });

        const track = glideSlides.parentElement;
        if(!track) return;
        track.addEventListener('focusin', (e: FocusEvent) => {
            let unscrolled = track.scrollLeft === 0;
            track.scrollLeft = 0;
            // webkit scrolls in the next task
            setTimeout(() => {
                if (track.scrollLeft === 0 && unscrolled)
                    return;
                track.scrollLeft = 0;
                const slide = (e.target as Element).closest('.glide__slide');
                const slideIndex = slides.indexOf(slide as any);
                // could be a clone slide
                if (slideIndex !== -1)
                    this.slider.go(`=${slideIndex}`);
            }, 0);
        });

        this.slider.on(['build.after', 'run.after'], () => {
            const activeSlide = this.root.querySelector('.glide__slide--active');
            const allSlides = this.root.querySelectorAll('app-hero-slide');
        
            if (!activeSlide) return;
        
            allSlides.forEach(slide => {
                const focusableElements = slide.querySelectorAll('button, a, input, textarea, select');
                focusableElements.forEach(el => {
                    el.setAttribute('tabindex', slide === activeSlide ? '0' : '-1');
                });
            });
        });

        this.slider.on(['build.after', 'run.after'], () => {
            const glideTrack = this.root.querySelector('.st-slides');
            const activeSlide = this.root.querySelector('.glide__slide--active') as HTMLElement;

            if(!glideTrack) return;

            Array.from(glideTrack.children).forEach((slide: HTMLElement) => {
                //console.log("glideTrack.children: ", glideTrack.children);
                slide.setAttribute('tabindex', '-1');
            });

            activeSlide.setAttribute('tabindex', '0');

            if (activeSlide.nextElementSibling)
                (activeSlide.nextElementSibling as HTMLElement).setAttribute('tabindex', '0');
        });

        this.slider.on('move.after', () => {
            const activeSlide = this.root.querySelector('.glide__slide--active') as HTMLElement;
        
            this.root.querySelectorAll('app-hero-slide').forEach(slide => {
                (slide as HTMLElement).setAttribute('tabindex', '-1');
            });
        
            activeSlide.setAttribute('tabindex', '0');
        });

        this.slider.on('mount.after', () => {
            globalThis.imageLoader.initialize();
        });

        this.slider.on('move.after', () => {
            if (this.pauseIcon === "play") {
                this.pauseIcon = "play";
                this.pauseLabel = "Play";
                setTimeout(() => {
                    this.slider.pause();
                }, 1000);
            } else {
                this.slider.play();
                this.pauseIcon = "pause";
                this.pauseLabel = "Pause";
            }
        });       

        this.slider.mount();       

        this.initialRenderComplete = true;
    }

    componentDidRender() {
        const carousel = this.root.querySelector('.st-carousel');
        const slides = this.root.querySelector('.st-slides');
        const prevButton = this.root.querySelector('button[data-glide-dir="<"]');
        const nextButton = this.root.querySelector('button[data-glide-dir=">"]');

        if (carousel) carousel.setAttribute('id', this.sliderId);
        if (slides) slides.setAttribute('id', `${this.sliderId}-slides`);
        if (prevButton) prevButton.setAttribute('aria-controls', `${this.sliderId}-slides`);
        if (nextButton) nextButton.setAttribute('aria-controls', `${this.sliderId}-slides`);

        if (!this.initialRenderComplete) {
            this.setUp();
        }
    }

    render() {
        return (
            <div class="app-hero-slider hero-layout large">
                <div class="glide st-carousel">
                    <div class="st-carousel-inner">
                        <div class="glide__track" data-glide-el="track">
                            <slot/>
                        </div>

                        <div class="slider-arrow arrow-prev controls" data-glide-el="controls">
                            <button data-glide-dir="<" aria-label="Previous Slide">
                                <app-icon icon="chevron-right"></app-icon>
                            </button>
                        </div>
                        <div class={`bullets-wrapper ${this.displayBulletNavExternally ? 'external' : 'internal'}`}>

                                <div class="glide__bullets" data-glide-el="controls[nav]"></div>                            

                        </div>
                        <div class="slider-arrow arrow-next controls" data-glide-el="controls">
                            <button data-glide-dir=">" aria-label="Next Slide">
                                <app-icon icon="chevron-right"></app-icon>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
